/**
 * Duplicate-application collapse for one event’s application list (same normalized email).
 *
 * **Stats contract (AdminDatesPage):** “Total Applications” and “Avg Applications/Event”
 * must use the full filtered application arrays (`eventsWithApplications`), **not** `visible`.
 * Display-only; must not change funnel metrics.
 *
 * **Scope:** Only within the array passed in — never merge across events.
 *
 * @see docs/APP_UI_SPEC.md — C2
 */
import { compareApplicationsAlphabetically } from './adminDatesApplications.js';

export function normalizeEmailForDedup(email) {
  if (email == null || typeof email !== 'string') return '';
  return email.trim().toLowerCase();
}

function compareCreatedAtDesc(a, b) {
  const ta = new Date(a.created_at || 0).getTime();
  const tb = new Date(b.created_at || 0).getTime();
  return tb - ta;
}

function compareCreatedAtAsc(a, b) {
  const ta = new Date(a.created_at || 0).getTime();
  const tb = new Date(b.created_at || 0).getTime();
  return ta - tb;
}

/**
 * @param {Array<{ id: string, email?: string|null, created_at?: string|null }>} applications
 * @returns {{
 *   visible: typeof applications,
 *   hiddenCountByApplicationId: Record<string, number>,
 *   hiddenDuplicatesByVisibleId: Record<string, typeof applications>
 * }}
 */
export function collapseDuplicateApplicationsByEmail(applications) {
  if (!applications?.length) {
    return {
      visible: [],
      hiddenCountByApplicationId: {},
      hiddenDuplicatesByVisibleId: {},
    };
  }

  /** @type {Map<string, typeof applications>} */
  const byEmail = new Map();
  for (const app of applications) {
    const key = normalizeEmailForDedup(app.email);
    if (!byEmail.has(key)) byEmail.set(key, []);
    byEmail.get(key).push(app);
  }

  const visible = [];
  /** @type {Record<string, number>} */
  const hiddenCountByApplicationId = {};
  /** @type {Record<string, typeof applications>} */
  const hiddenDuplicatesByVisibleId = {};

  for (const [, group] of byEmail) {
    const sortedNewestFirst = [...group].sort(compareCreatedAtDesc);
    const [newest, ...older] = sortedNewestFirst;
    visible.push(newest);
    if (older.length > 0) {
      hiddenCountByApplicationId[newest.id] = older.length;
      hiddenDuplicatesByVisibleId[newest.id] = [...older].sort(compareCreatedAtAsc);
    }
  }

  visible.sort(compareApplicationsAlphabetically);

  return { visible, hiddenCountByApplicationId, hiddenDuplicatesByVisibleId };
}
